import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    OnInit,
    signal,
    viewChild,
    WritableSignal,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';

import { ComplexModalTemplate } from '@connect-services/client/components/complexe-modal/complex-modal-template.component';
import { ExportDepositModalSecondStepComponent } from '@connect-services/client/components/modals/deposit/export-deposit-modal/export-deposit-modal-second-step/export-deposit-modal-second-step.component';
import { ModalResultEnum } from '@connect-services/client/models/common.model';
import { DepositFolderService } from '@connect-services/client/services/deposit-folder.service';
import { CheckboxState } from '@connect-services/src/lib/components/checkbox/checkbox.component';
import { GroupBucket, SelectOption } from '@connect-services/src/lib/components/select/select.component';
import {
    NO_GROUP,
    SelectByGroupComponent,
} from '@connect-services/src/lib/components/select-by-group/select-by-group.component';
import { DepositFolder } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import { LoadingComponent } from '@shared/client/components/loading/loading.component';

type StepData = {
    condition: () => boolean;
    title: string;
};

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        ComplexModalTemplate,
        ExportDepositModalSecondStepComponent,
        LoadingComponent,
        RessourcesModule,
        SelectByGroupComponent,
    ],
    selector: 'app-export-deposit-modal',
    templateUrl: './export-deposit-modal.component.html',
})
export class ExportDepositModalComponent implements OnInit {
    public readonly currentStep: WritableSignal<number> = signal<number>(1);

    public readonly depositsFolderOptions = computed((): SelectOption<DepositFolder>[] => {
        return (
            this.depositFolderList().map((item: DepositFolder) => ({
                label: item.name,
                state: CheckboxState.UNCHECKED,
                value: item,
            })) ?? []
        );
    });
    public readonly secondStep = viewChild.required<ExportDepositModalSecondStepComponent>('secondStep');
    public readonly selectedDeposits: WritableSignal<SelectOption<DepositFolder>[]> = signal([]);

    protected readonly CheckboxState = CheckboxState;
    protected readonly currentStepData = computed((): StepData => this.stepData()[this.currentStep() - 1]);
    protected readonly depositFolderList: WritableSignal<DepositFolder[]> = signal([]);
    protected dialogRef = inject(MatDialogRef<ExportDepositModalComponent>);
    protected readonly optionsByGroup = computed<GroupBucket<DepositFolder> | null>(() => {
        if (!this.depositFolderList()?.length) {
            return null;
        }

        return this.depositFolderList()!.reduce((acc: GroupBucket<DepositFolder>, depositFolder: DepositFolder) => {
            const depositFolderOption = {
                label: depositFolder.name,
                state: CheckboxState.UNCHECKED,
                value: depositFolder,
            };
            const groupName = depositFolder.standard?.name ?? NO_GROUP;

            if (!acc[groupName]) {
                acc[groupName] = [];
            }

            acc[groupName].push(depositFolderOption);

            return acc;
        }, {});
    });
    protected readonly stepData = computed((): StepData[] => [
        {
            condition: () => !!this.selectedDeposits().length,
            title: `${this.translocoService.translate('step', { NUMBER: 1 })} - ${this.translocoService.translate('dataSelection')}`,
        },
        {
            condition: () => true,
            title: `${this.translocoService.translate('step', { NUMBER: 2 })} - ${this.translocoService.translate('exportConfiguration')}`,
        },
    ]);

    private readonly depositFolderService = inject(DepositFolderService);
    private readonly translocoService = inject(TranslocoService);

    public async ngOnInit(): Promise<void> {
        this.depositFolderList.set(await this.depositFolderService.getAllDepositFolders());
    }

    public onClickEvent(modalResult: ModalResultEnum): void {
        if (modalResult === ModalResultEnum.CANCEL) {
            this.dialogRef.close();

            return;
        }

        const exportData = this.secondStep().getExportData();

        this.dialogRef.close(exportData);
    }
}
